
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import DragDots from "./DragDots.vue";
import SpacingBars from "./SpacingBars.vue";
import ColumnDragSizer from "./ColumnDragSizer.vue";
import NodeTitlebar from "./NodeTitlebar.vue";
import NodeHoverControls from "./NodeHoverControls.vue";
import NodeContentEditable from "@/components/Canvas/OverlaidUI/NodeContentEditable.vue";
import {
  documentStore,
  uiStore,
  singularityStore
} from "@/store/store-accessor";
import {
  getDomElementFromTreeNode,
  shouldHideElementNodeControls
} from "@/lib/canvas/helpers";
import { findNode } from "@/lib/tree/tree";
import { TreeNode } from "@/types/tree";
import NodeOnboardingHoverControls from "@/components/Canvas/OverlaidUI/Onboarding/NodeOnboardingHoverControls.vue";
import NodeGhostTitlebar from "@/components/Canvas/OverlaidUI/NodeGhostTitlebar.vue";
import NodeSingularityActive from "@/components/Canvas/OverlaidUI/Singularity/NodeSingularityActive.vue";
import NodeSingularityHover from "@/components/Canvas/OverlaidUI/Singularity/NodeSingularityHover.vue";
import NodeSingularityContentEditable from "@/components/Canvas/OverlaidUI/Singularity/NodeSingularityContentEditable.vue";
import NodeSingularityTitlebar from "@/components/Canvas/OverlaidUI/Singularity/NodeSingularityTitlebar.vue";
import FutureLayerNodeChrome from "@/components/Canvas/OverlaidUI/Singularity/FutureLayerNodeChrome.vue";
import { getFutureLayerElementBehavior } from "@/futurelayer/canvas/element-behavior/element-behavior";

@Component({
  components: {
    NodeContentEditable,
    DragDots,
    SpacingBars,
    ColumnDragSizer,
    NodeTitlebar,
    NodeGhostTitlebar,
    NodeHoverControls,
    NodeOnboardingHoverControls,
    NodeSingularityHover,
    NodeSingularityActive,
    NodeSingularityContentEditable,
    NodeSingularityTitlebar,
    FutureLayerNodeChrome
  }
})
export default class NodeChrome extends Vue {
  observer?: MutationObserver;
  hoverObserver?: MutationObserver;
  hideTitleBar = false;
  hideHoverControls = false;

  get isSingularityMode() {
    return uiStore.isSingularityMode;
  }

  get isSingularityPreviewMode() {
    return singularityStore.previewMode;
  }

  get isOnboardingMode() {
    return window.Breakdance.isOnboarding;
  }

  get element() {
    return uiStore.activeElement || false;
  }

  get activeElement() {
    return uiStore.activeElement;
  }

  get hoveredElement() {
    if (uiStore.lastHoveredOnCanvasElementId) {
      return findNode(
        documentStore.document.tree,
        uiStore.lastHoveredOnCanvasElementId
      );
    }

    return false;
  }

  get classes() {
    let prefix = null;

    if (this.hideTitleBar && this.hideHoverControls) {
      prefix = "all";
    } else if (this.hideHoverControls) {
      prefix = "hover";
    } else if (this.hideTitleBar) {
      prefix = "titlebar";
    }

    prefix ? `node-chrome--hide-${prefix}` : null;

    return {
      "node-chrome": true,
      [`node-chrome--hide-${prefix}`]: prefix,
      "node-chrome--singularity": this.isSingularityMode
    };
  }

  @Watch("element")
  onElementSelected() {
    this.observer?.disconnect();
    if (!this.element) return;

    this.observer = this.observePositionChange(this.element, target => {
      this.hideTitleBar = shouldHideElementNodeControls(target);
    });
  }

  @Watch("hoveredElement")
  onHoveredElementSelected() {
    this.hoverObserver?.disconnect();
    if (!this.hoveredElement) return;

    this.hoverObserver = this.observePositionChange(
      this.hoveredElement,
      target => {
        this.hideHoverControls = shouldHideElementNodeControls(target);
      }
    );
  }

  beforeDestroy() {
    this.destroyObserver();
  }

  observePositionChange(
    element: TreeNode | false,
    callback: (target: HTMLElement) => void
  ) {
    if (!element) return;
    const node = getDomElementFromTreeNode(element);

    if (!node) return;

    const onMutate = (mutations: MutationRecord[]) => {
      mutations.forEach(record => {
        const target = record.target as HTMLElement;
        callback(target);
      });
    };

    const observer = new MutationObserver(onMutate);

    const options = {
      attributes: true,
      attributeFilter: ["class"]
    };

    observer.observe(node, options);
    return observer;
  }

  destroyObserver() {
    this.observer?.disconnect();
    this.hoverObserver?.disconnect();
  }
}
